import axios from 'axios';
import { installActivityTracking, withActivityHeader } from './userActivity';
import {
  ATTEMPT_HEADER, clearAttemptId, getAttemptId, markRefreshOk, refreshedRecently,
} from './refreshAttempt';
import { hasPendingLogout, isAuthRejection, isSessionExpired, markSessionExpired } from './sessionEvents';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global 401 interceptor: try to refresh once, then replay the failed request.
// This fixes intermittent "Not authenticated" errors after the 15-min access
// token expires while the refresh cookie is still valid.
let refreshing = null;

// One refresh at a time, across tabs when the browser offers Web Locks. The
// SAME attempt id is re-sent until an answer has been seen, so a lost response
// can be recovered by the server (see refreshAttempt.js).
const doRefresh = async () => {
  const exec = async () => {
    if (refreshedRecently()) return;            // another tab has just renewed the session
    const attempt = getAttemptId();
    try {
      await axios.post(`${API}/auth/refresh`, {}, {
        withCredentials: true,
        headers: attempt ? { [ATTEMPT_HEADER]: attempt } : {},
      });
      markRefreshOk();
      clearAttemptId();
    } catch (e) {
      // A refusal ends the attempt; a network error / 5xx / 429 keeps it for the retry.
      if (isAuthRejection(e)) clearAttemptId();
      throw e;
    }
  };
  const locks = typeof navigator !== 'undefined' ? navigator.locks : null;
  return locks && locks.request ? locks.request('ridalo-refresh', exec) : exec();
};

installActivityTracking();
axios.interceptors.request.use(withActivityHeader);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;

    if (!original || status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    // Never try to refresh on the refresh/me/login endpoints themselves.
    const url = (original.url || '').toString();
    if (url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/logout')) {
      return Promise.reject(error);
    }

    // The refresh was rejected a moment ago: do not hammer /auth/refresh from
    // every poller (5 s chat, 30 s bell), just report the 401.
    if (isSessionExpired()) {
      return Promise.reject(error);
    }

    // A sign-out that could not reach the server is waiting to be replayed:
    // never renew the session behind its back.
    if (hasPendingLogout()) {
      return Promise.reject(error);
    }

    original._retry = true;

    try {
      if (!refreshing) {
        refreshing = doRefresh();
      }
      await refreshing;
    } catch (e) {
      refreshing = null;
      if (isAuthRejection(e)) {
        // The server refused the refresh token (expired / invalid / account
        // deactivated): the session is really over.
        markSessionExpired();
        return Promise.reject(error);
      }
      // Network error, timeout or 5xx: says nothing about the session. Surface
      // THAT error (not a 401) so callers never mistake it for a sign-out.
      return Promise.reject(e);
    } finally {
      // Clear the shared promise after it settles so future 401s can trigger a new refresh
      refreshing = null;
    }

    // Retry the original request with credentials
    return axios({ ...original, withCredentials: true });
  }
);

export default axios;
